import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../core/common/header/PageHeader";
import {
  FaHome,
  FaChartBar,
  FaSpinner,
  FaEye,
  FaDownload,
  FaTrophy,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Assignment = {
  id: number;
  examSetId: number;
  examSetName: string;
  level: string;
  variant: string;
  batchId: number;
  batchName: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  totalStudents: number;
};

type Result = {
  attemptId: number;
  studentId: number;
  studentName: string;
  studentEmail: string;
  submittedAt: string;
  totalScore: number;
  maxScore: number;
  percentage: number;
  isPassed: boolean;
  lesenScore: number;
  lesenMax: number;
  horenScore: number;
  horenMax: number;
  schreibenScore: number;
  schreibenMax: number;
  overallFeedback: string | null;
};

const MentorExamResults: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchResults();
  }, [assignmentId]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      // Fetch assignment details
      const assignmentRes = await fetch(
        `${API_BASE_URL}/api/mentor/exams/assignments/${assignmentId}/details`,
        {
          credentials: "include",
        }
      );
      if (assignmentRes.ok) {
        const assignmentData = await assignmentRes.json();
        setAssignment(assignmentData);
      }

      // Fetch results
      const resultsRes = await fetch(
        `${API_BASE_URL}/api/mentor/exams/assignments/${assignmentId}/results`,
        {
          credentials: "include",
        }
      );
      if (resultsRes.ok) {
        const resultsData = await resultsRes.json();
        setResults(resultsData);
      } else {
        setError("Failed to load results");
      }
    } catch (err) {
      console.error("Error fetching results:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportToCSV = () => {
    if (!assignment) return;

    const headers = [
      "Student Name",
      "Email",
      "Submitted",
      "Total Score",
      "Max Score",
      "Percentage",
      "Status",
      "Lesen Score",
      "Hören Score",
      "Schreiben Score",
      "Feedback",
    ];

    const rows = results.map((result) => [
      result.studentName,
      result.studentEmail,
      formatDate(result.submittedAt),
      result.totalScore,
      result.maxScore,
      `${result.percentage.toFixed(1)}%`,
      result.isPassed ? "PASSED" : "FAILED",
      `${result.lesenScore}/${result.lesenMax}`,
      `${result.horenScore}/${result.horenMax}`,
      `${result.schreibenScore}/${result.schreibenMax}`,
      result.overallFeedback || "",
    ]);

    const csv = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${assignment.batchName}-${assignment.examSetName}-results.csv`;
    a.click();
  };

  // Calculate statistics
  const totalAttempts = results.length;
  const passedCount = results.filter((r) => r.isPassed).length;
  const failedCount = totalAttempts - passedCount;
  const passRate = totalAttempts > 0 ? (passedCount / totalAttempts) * 100 : 0;
  const avgScore =
    totalAttempts > 0
      ? results.reduce((sum, r) => sum + r.percentage, 0) / totalAttempts
      : 0;

  // Module averages
  const avgLesen =
    totalAttempts > 0
      ? results.reduce((sum, r) => sum + (r.lesenScore / r.lesenMax) * 100, 0) /
        totalAttempts
      : 0;
  const avgHoren =
    totalAttempts > 0
      ? results.reduce((sum, r) => sum + (r.horenScore / r.horenMax) * 100, 0) /
        totalAttempts
      : 0;
  const avgSchreiben =
    totalAttempts > 0
      ? results.reduce(
          (sum, r) => sum + (r.schreibenScore / r.schreibenMax) * 100,
          0
        ) / totalAttempts
      : 0;

  // Top performers
  const topPerformers = [...results]
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaChartBar />}
        title="Exam Results"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Exams", to: "/mentor/exam-management" },
          {
            label: assignment
              ? `${assignment.batchName} - ${assignment.examSetName}`
              : "Results",
          },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Assignment Info */}
        {assignment && (
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600 font-medium">Exam</p>
                <p className="text-lg font-bold text-gray-900">
                  {assignment.examSetName}
                </p>
                <p className="text-xs text-gray-500">{assignment.variant}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Level</p>
                <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold bg-blue-600 text-white mt-1">
                  {assignment.level}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Batch</p>
                <p className="text-lg font-bold text-gray-900">
                  {assignment.batchName}
                </p>
                <p className="text-xs text-gray-500">
                  {assignment.totalStudents} students
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Schedule</p>
                <p className="text-xs text-gray-700 mt-1">
                  {formatDate(assignment.scheduledStartTime)}
                </p>
                <p className="text-xs text-gray-500">to</p>
                <p className="text-xs text-gray-700">
                  {formatDate(assignment.scheduledEndTime)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Submissions</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {totalAttempts}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  of {assignment?.totalStudents} students
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaUsers className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Passed</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {passedCount}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {passRate.toFixed(1)}% pass rate
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FaCheckCircle className="text-green-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Failed</p>
                <p className="text-3xl font-bold text-red-600 mt-1">
                  {failedCount}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {(100 - passRate).toFixed(1)}% failure rate
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <FaTimesCircle className="text-red-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Avg Score</p>
                <p className="text-3xl font-bold text-[#009fe3] mt-1">
                  {avgScore.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">class average</p>
              </div>
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <FaChartBar className="text-[#009fe3] text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Module Performance & Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Module Performance */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Module Performance
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Lesen (Reading)
                  </span>
                  <span className="text-sm font-bold text-blue-600">
                    {avgLesen.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${avgLesen}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Hören (Listening)
                  </span>
                  <span className="text-sm font-bold text-purple-600">
                    {avgHoren.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-purple-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${avgHoren}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Schreiben (Writing)
                  </span>
                  <span className="text-sm font-bold text-green-600">
                    {avgSchreiben.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${avgSchreiben}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaTrophy className="text-yellow-500" />
              Top Performers
            </h3>
            {topPerformers.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No submissions yet
              </p>
            ) : (
              <div className="space-y-3">
                {topPerformers.map((performer, index) => (
                  <div
                    key={performer.attemptId}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {performer.studentName}
                        </div>
                        <div className="text-xs text-gray-600">
                          {formatDate(performer.submittedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg text-green-600">
                        {performer.percentage.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-600">
                        {performer.totalScore}/{performer.maxScore}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Export Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={exportToCSV}
            disabled={results.length === 0}
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FaDownload />
            Export to CSV ({results.length} results)
          </button>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {error && (
            <div className="p-4 bg-red-50 border-b border-red-200 text-red-700">
              {error}
            </div>
          )}

          {results.length === 0 ? (
            <div className="text-center py-12">
              <FaUsers className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No Submissions Yet
              </h3>
              <p className="text-gray-500 text-sm">
                Students haven't submitted their exams yet
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Student
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Score
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Modules
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                      Submitted
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {results.map((result, index) => (
                    <tr
                      key={result.attemptId}
                      className={`${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-900">
                            {result.studentName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {result.studentEmail}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="font-bold text-lg text-gray-900">
                          {result.totalScore}/{result.maxScore}
                        </div>
                        <div className="text-xs text-gray-600">
                          {result.percentage.toFixed(1)}%
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Lesen:</span>
                            <span className="font-semibold text-blue-600">
                              {result.lesenScore}/{result.lesenMax}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Hören:</span>
                            <span className="font-semibold text-purple-600">
                              {result.horenScore}/{result.horenMax}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Schreiben:</span>
                            <span className="font-semibold text-green-600">
                              {result.schreibenScore}/{result.schreibenMax}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {result.isPassed ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            <FaCheckCircle size={12} />
                            PASSED
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            <FaTimesCircle size={12} />
                            FAILED
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {formatDate(result.submittedAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() =>
                            navigate(`/student/exam/${result.attemptId}/result`)
                          }
                          className="inline-flex items-center gap-1 px-3 py-2 bg-[#009fe3] text-white rounded-md hover:bg-cyan-600 transition-colors text-sm font-semibold"
                        >
                          <FaEye size={14} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MentorExamResults;
